import { Injectable, EventEmitter, PLATFORM_ID, Inject } from "@angular/core";
import { Observable, BehaviorSubject, of } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ShellDomainProvider } from "../utility/shell-domain.provider";
import { PlatformDataService } from "../pds/platform-data.service";
import { SqlQueryBuilderService } from "../query-builder/sql-query-builder.service";
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { map, switchMap } from "rxjs/operators";
import { EcapCookieService } from '../cookie/ecap-cookie.service';
import { JwtHelperService } from '@auth0/angular-jwt';

export const Message: any = {
  DefaultMessage: 'DEFAULT_MESSAGE',
  LogOutMessage: 'LOGOUT_MESSAGE',
  PersonError: 'PERSON_ERROR'
}
@Injectable()
export class UserInfoService {
  private tenantInfo: any;
  public userInfo: any = {};
  public personData: any = {};
  public userData: any;
  public userInfoEvent: EventEmitter<any> = new EventEmitter<any>();
  private platformIdValue: string;
  private isPlatformServer: boolean;
  public currentUserDataSource = new BehaviorSubject<any>(Message.DefaultMessage);
  currentUserData = this.currentUserDataSource.asObservable();
  OnProfileImageChange: EventEmitter<any> = new EventEmitter<any>();
  profileImageRequestCompleted: EventEmitter<any> = new EventEmitter<any>();
  public currentUserEvent = new BehaviorSubject<any>(Message.DefaultMessage);
  currentUser = this.currentUserEvent.asObservable();

  public userFirstTimeLogin = new BehaviorSubject<any>(Message.DefaultMessage);
  currentUserFirstTimeLogin = this.userFirstTimeLogin.asObservable();

  public userPersonDataNotFound = new BehaviorSubject<string>(Message.DefaultMessage);
  currentUserPersonDataNotFound = this.userPersonDataNotFound.asObservable();

  public afterLoginCallback: any;
  logedUserRoles: any;

  header: any = new HttpHeaders({
    "Content-Type": "application/json"
  });

  constructor(@Inject(PLATFORM_ID) platformId: string,
    private http: HttpClient,
    private _cookieService: EcapCookieService,
    private pdsService: PlatformDataService,
    private sqlBuilder: SqlQueryBuilderService) {
    this.platformIdValue = platformId;
    this.isPlatformServer = isPlatformServer(platformId);
  }

  changeCurrentUserData(data: any) {
    this.currentUserDataSource.next(data);
  }

  changeUserFirstTimeLoginStatus(data: any) {
    this.userFirstTimeLogin.next(data);
  }

  getLoggedInUser(): Observable<any> {
    const header = new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      'Pragma': 'no-cache',
      'Expires': '0'
    });

    return this.http.get(ShellDomainProvider["api"].SecurityIdentity + "Authentication/GetLoggedInUser" + "?cacheBuster=" + new Date().getTime(),
      { headers: header, withCredentials: true })
      .pipe(map((response: any) => {
        this.setUserInfo(response);
        return response;
      }, (errorResponse: any) => {
        return errorResponse;
      }));
  }

  private setRolesForPersonaUser() {
    if (this.isPlatformServer) {
      if (process.env["ecap-user-roles"]) {
        this.logedUserRoles = JSON.parse(process.env["ecap-user-roles"]);
      }
    } else {
      const expectedCookie = this._cookieService.getCookie("ecap-user-roles");
      if (expectedCookie) {
        const cookieParsedRoles = JSON.parse(expectedCookie);
        if (cookieParsedRoles && cookieParsedRoles.length) {
          this.userInfo.Roles = cookieParsedRoles;
          this.logedUserRoles = cookieParsedRoles;
        }
        else {
          this.setRolesToCookie(this.userInfo.Roles);
        }
      } else {
        this.setRolesToCookie(this.userInfo.Roles);
      }
    }
  }

  private setRolesToCookie(userRoles) {
    this.logedUserRoles = userRoles;
  }

  private userFirstTimeLoginEvent(isLoginClick) {
    this.userFirstTimeLogin.next({
      IsLoginClick: isLoginClick,
      FirstName: this.userInfo["FirstName"],
      LastName: this.userInfo["LastName"],
      Salutation: this.userInfo["Salutation"],
      Roles: this.userInfo["Roles"],
      UserId: this.userInfo["UserId"],
      UserName: this.userInfo["UserName"],
      PersonId: this.userInfo["PersonId"],
      LogInCount: this.userInfo["LogInCount"],
      EligibleForServiceCheck: this.userInfo["EligibleForServiceCheck"],
      CustomerId: this.userInfo["CustomerId"],
      AdvisorNr: this.userInfo["AdviserNr"]  // Spelling mistake(AdviserNr) in Person table. But we are returning with the correct(AdvisorNr) spelling
    });
  }

  setLoggedInUser(isLoginClick?: boolean) {
    console.log('Is internal login: '+isLoginClick);
    this.getLoggedInUser().subscribe((response: any) => {
      if (!isLoginClick)
        this.currentUserEvent.next(response);
      if (response.IsSuccess == true) {
        this.userInfo = response;
        this.userInfo.UserName = response.Email;

        if (response && response.PersonaEnabled)
          this.setRolesForPersonaUser();

        this.userData = response;

        this.sendUserInfoEvent(this.userInfo);

        this.getFullPersonInfo((personData: any) => {
          if (isLoginClick && this.platformIdValue === "browser")
          this._cookieService.eraseAndSetCookie('IS_LOGIN_CLICK','yes');

          if (!personData) {
            this.userPersonDataNotFound.next(Message.PersonError);
            return;
          }

          /**
           * This code block gets "storageBucketBaseUrl" from business environment file.
           * If there is any "storageBucketBaseUrl" available then 1
           * 1. personData.ProfileImage is concatenated with "storageBucketBaseUrl" to make it a complete url to view image
           */
          const storageBucketBaseUrl = ShellDomainProvider['api'].storageBucketBaseUrl;
          if (storageBucketBaseUrl && personData.ProfileImage) {
            const splittedimagePath = personData.ProfileImage.split('/');
            if (splittedimagePath[0] === ShellDomainProvider['api'].Client_ID) {
              personData.ProfileImage = storageBucketBaseUrl.concat(personData.ProfileImage);
            }
          }

          this.personData = personData;

          for (let i in personData)
            this.userInfo[i] = personData[i];

          this.userInfo["PersonId"] = this.userInfo["ItemId"];
          this.userInfo["PersonData"] = personData;
          this.userInfo["ExternalProviderUserId"] = this.userData["ExternalProviderUserId"];
          this.userData = this.userInfo;

          this.sendUserInfoEvent(this.userInfo);
          this.currentUserEvent.next(this.userInfo);
          this.userFirstTimeLoginEvent(isLoginClick);
        });
      }
    }, errorResponse => {
      console.log("==========Set Logged In User Error=======");
      throw new Error(errorResponse.message);
    }
    );
  }

  public getUserRoles() {
    return this.http
      .post(
        ShellDomainProvider['api'].Security + "SecurityQuery/GetRoles",
        null,
        {
          headers: this.header,
          withCredentials: true,
          observe: "response"
        }
      )
      .pipe(map(response => response));
  }

  getUserInfoEvent() {
    return this.userInfoEvent;
  }

  getUserInfo() {
    return this.userInfo;
  }

  setUserInfo(user) {
    this.userInfo = user;
    if (this.personData) {
      this.userInfo["PersonData"] = this.personData;
    }
  }

  resetUserInfo() {
    this.userInfo = {};
    this.personData = {};
    this.userData = {};
    this.currentUserEvent.next(Message.LogOutMessage);
  }

  sendUserInfoEvent(data: any) {
    this.userInfoEvent.emit(data);
  }

  getIdentity(): Observable<any> {
    const header = new HttpHeaders({
      "Content-Type": "application/json"
    });
    return this.http
      .get(ShellDomainProvider["api"].Identity, {
        headers: header,
        withCredentials: true
      })
      .pipe(
        map(
          response => {
            return response;
          },
          errorResponse => {
            return errorResponse;
          }
        )
      );
  }

  getTenantInfo() {
    return this.tenantInfo;
  }

  setTenantInfo() {
    this.getIdentity().subscribe((response: any) => {
      this.tenantInfo = response;
      this.getMaintenanceStatus(response['SiteId'])
    });
  }
  getMaintenanceStatus(siteId: string) {
    var maintenanceManagerUrl=ShellDomainProvider["api"] && ShellDomainProvider["api"].MaintenanceManagerURL ? ShellDomainProvider["api"].MaintenanceManagerURL : null;
    if(maintenanceManagerUrl){
      this.http.get(maintenanceManagerUrl+'?SiteId=' + siteId).subscribe(((response: any) => {
        if (response && response.maintenanceModeOn) {
          const iframe = document.createElement('iframe');
          iframe.setAttribute('width', '100%');
          iframe.setAttribute('height', '100%');
          iframe.setAttribute('src', `${maintenanceManagerUrl}/${response._id}/template`);
          iframe.setAttribute('frameborder', '0');
          iframe.setAttribute('allowfullscreen', '');
          document.body.replaceChildren(iframe);
          return of({})
        }
      }))
    }
  }
  setCurrentLanguageToToken(langKey): Observable<any> {
    let langValue = "";
    let changePersonLanguage=false;
    const header = new HttpHeaders({
      "Content-Type": "application/json"
    });

    if(ShellDomainProvider["api"] && ShellDomainProvider["api"].changePersonLanguage) changePersonLanguage = ShellDomainProvider["api"].changePersonLanguage;
    if (ShellDomainProvider["api"].language && ShellDomainProvider["api"].language.length) {
      const selectedLang = ShellDomainProvider["api"].language.find(x => x["id"] == langKey);
      if (selectedLang)
        langValue = selectedLang["value"];
      else
        langValue = this.getDefaultLanguageValue(langKey);
    }
    else
      langValue = this.getDefaultLanguageValue(langKey);

    if (!langValue) return of({ StatusCode: 1 });

    let refresh_token;

    if (this.isPlatformServer)
      refresh_token = process.env.REFRESH_TOKEN;
    else
      refresh_token = this._cookieService.getCookie("REFRESH_TOKEN");

    const payload =  { Language: langValue, RefreshToken: refresh_token,ChangePersonLanguage:changePersonLanguage };

    if(ShellDomainProvider["api"] && ShellDomainProvider["api"].ChangePersonLanguageForActiveSessions) {
      payload["UpdateAllActiveTokens"] = true;
    }

    return this.http
      .post(ShellDomainProvider["api"].SecurityIdentity + "Authentication/ChangeLanguage",
        payload,
        { headers: header, withCredentials: true, observe: "response" })
      .pipe(map((result: any) => {
        if (result.body.ErrorMessages && !result.body.ErrorMessages.length) {
          return result.body;
        }
      }));
  }

  public getDefaultLanguageValue(langKey) {
    let langValue = '';
    switch (langKey) {
      case 'en':
        langValue = langKey + '-US';
        break;
      case 'de':
        langValue = langKey + '-DE';
        break;
      case 'fr':
        langValue = langKey + '-FR';
        break;
      case 'cz':
        langValue = 'cs-CZ';
        break;
      case 'it':
        langValue = langKey + '-IT';
        break;
    }

    return langValue;
  }

  getFullPersonInfo(callback: any) {
    this.getPersonConnection().subscribe((personId: string) => {
      if (personId) {
        this.getPersonInfo(personId).subscribe(personInfo => {
          callback(personInfo);
        });
      }
      else
        callback(null);
    });
  }

  getLoggedInPersonInfo(): any {
    return this.getPersonConnection().pipe(
      switchMap((personId: string) => {
        if (!personId) return of(null);
        return this.getPersonInfo(personId);
      }),
      map((personInfo) => {
        return personInfo;
      })
    );
  }

  getPersonConnection(userId?: string): Observable<any> {
    let getConnectQuery = this.sqlBuilder.prepareGetConnectionQuery(
      "User",
      userId || this.userInfo.UserId,
      "Person",
      "",
      false,
      []
    );
    getConnectQuery["PageNumber"] = 0;
    getConnectQuery["PageLimit"] = 1;
    return this.pdsService.getConnections(getConnectQuery)
      .pipe(map((response: Response) => {
        if (response.body["Results"].length) {
          return response.body["Results"][0]["ChildEntityID"];
        } else {
          return null;
        }
      })
      );
  }

  getPersonInfo(personId: string, mFields?: string[]): Observable<any> {
    let filter = [
      {
        property: "PersonId",
        operator: "=",
        value: personId
      }
    ];
    let fields = [];
    if(mFields){
      fields = mFields;
    }else{
      fields = mFields || ShellDomainProvider["api"].PersonFields || [
        "ItemId",
        "CreatedBy",
        "Tags",
        "Designation",
        "Nationality",
        "MarketName",
        "PhoneNumber",
        "Email",
        "OfficeCountryCode",
        "ProfileImage",
        "ProfileImageId"
      ];
    }

    let query = this.sqlBuilder.prepareQuery(
      "DeltaEmployee",
      fields,
      filter,
      null,
      0,
      1
    );
    return this.pdsService.getBySqlFilter("DeltaEmployee", query, null).pipe(
      map((response: any) => {
        if (
          response.body &&
          response.body.Results &&
          response.body.Results.length
        ) {
          return response.body.Results[0];
        } else {
          return null;
        }
      })
    );
  }

  updateLoggedInUserInfo(updatedUserInfo) {
    for (let i in updatedUserInfo) {
      this.userInfo[i] = updatedUserInfo[i];
    }
  }

  registerAfterLoginCallback(thiscallback) {
    this.afterLoginCallback = thiscallback;
  }

  getUserById(userId) {
    return this.http.get(
      ShellDomainProvider["api"].Security + "SecurityQuery/GetUserById",
      {
        headers: this.header,
        withCredentials: true,
        observe: "response",
        params: {
          UserId: userId
        }
      }
    );
  }

  callbackAfterLoginSuccess() {
    this.afterLoginCallback && this.afterLoginCallback.Callback();
  }

  updateUser(userInfo: object): Observable<any> {
    return this.http.post(
      ShellDomainProvider["api"].Security + "SecurityCommand/UpdateUser",
      userInfo,
      { headers: this.header, withCredentials: true, observe: "response" }
    );
  }

  geJwtDecodedUserData(access_token) {
    let user: any = {};
    const helper = new JwtHelperService();
    let decodeUser = helper.decodeToken(access_token);
    user['UserId'] = decodeUser.user_id;
    user['DisplayName'] = decodeUser.display_name;
    user['Roles'] = decodeUser.role;
    user['UserName'] = decodeUser.user_name;
    user['Email'] = decodeUser.email;
    user['PhoneNumber'] = decodeUser.phone_number;
    user['isLoggedIn'] = decodeUser.user_loggedin;
    return user;
  }

  getUserMinimalData(loggedInUser: any) {
    let user: any = {};
    user['UserId'] = loggedInUser.UserId;
    user['DisplayName'] = loggedInUser.DisplayName;
    user['Roles'] = loggedInUser.Roles;
    user['UserName'] = loggedInUser.UserName;
    user['Email'] = loggedInUser.Email;
    user['PhoneNumber'] = loggedInUser.PhoneNumber;
    user['isLoggedIn'] = true;
    return user;
  }

  updatePersonData(updatedPersonData) {
    for (let i in updatedPersonData) {
      this.userInfo["PersonData"][i] = updatedPersonData[i];
    }
  }

  resetSubscribeDataOnLogOut() {
    this.currentUserDataSource.next(Message.DefaultMessage);
    this.currentUserEvent.next(Message.DefaultMessage);
    this.userFirstTimeLogin.next(Message.DefaultMessage);
  }


  loginSynchronizer() {
    const logoutForm: any = document.createElement('form');
    const currentDomain = window.location.protocol + '//' + window.location.hostname;
    const currentDomainWithoutProtocol = window.location.hostname;
    logoutForm.action = currentDomain + '/auth?LogOff';
    logoutForm.method = 'post';
    const searchParams = location.search.substring(1);
    let landingUrlFinal = currentDomain;
    if (searchParams) {
      landingUrlFinal += '/login?' + searchParams;
    }
    const landingInput = document.createElement('input');
    landingInput.type = 'hidden';
    landingInput.name = 'Landing_Uri';
    landingInput.value = landingUrlFinal;

    const ssoDomainsInput = document.createElement('input');
    ssoDomainsInput.type = 'hidden';
    ssoDomainsInput.name = 'Logged_Off_SSO_Domains';
    ssoDomainsInput.value = currentDomainWithoutProtocol;

    logoutForm.appendChild(landingInput);
    logoutForm.appendChild(ssoDomainsInput);
    document.getElementsByTagName('body')[0].appendChild(logoutForm);
    logoutForm.submit();
  }

  loggedOutRequired(response: any) {
    if (window['shellDomain']['isSSo'] && isPlatformBrowser(this.platformIdValue) && window['shellDomain']['SingleSiteLogin'] && window['shellDomain']['IsMasterSite']) {
      const userInfo = this.geJwtDecodedUserData(response.access_token);
      const currentRoute = this.getCurrentPath();

      return !!(userInfo.isLoggedIn === 'True' && currentRoute && window['shellDomain']['ChildSiteNames'].includes(currentRoute.from));
    } else {
      return false;
    }
  }

  redirectToMasterSite(token?: any) {
    if (token && token.access_token) {
      const userInfo = this.geJwtDecodedUserData(token.access_token);
      if (userInfo['isLoggedIn'] === 'True')
        return;
    }

    if (ShellDomainProvider['api']['isSSo'] && !ShellDomainProvider['api']['IsMasterSite'] && ShellDomainProvider['api']['MasterSiteUri'] && ShellDomainProvider['api']['production']) {
      let masterDomain = `${window.location.protocol}//${ShellDomainProvider['api']['MasterSiteUri']}`;
      window.location.href = masterDomain;
    }
  }

  getCurrentPath(): any {
    const search = location.search.substring(1);
    return search && JSON.parse('{"' + decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');
  }
}

